<template>
  <div class="tenders__header">
    <div class="tenders__header-content">
      <div class="tenders__header-top">
        <!-- Phones: the view tabs don't fit, so the title names the view and switches it. -->
        <h1 v-if="switchesView" class="tenders__header-title">
          <BaseViewSwitcher :page="$t('menu.inbox')" :view="activeViewLabel" @click="$emit('switch-view')" />
        </h1>
        <h1 v-else class="tenders__header-title">{{ $t('menu.inbox') }}</h1>
      </div>
    </div>
    <!-- Keywords management lives here now (removed from the sidebar menu). -->
    <div class="tenders__header-actions">
      <button class="tenders__keywords" @click="$router.push(paths.keywords)">
        <div class="tenders__keywords-title">
          <BaseIcon name="type" />
          <span>{{ $t('menu.keywords') }}</span>
        </div>
        <span class="tenders__keywords-link">
          {{ $t('page.tenders.see-keywords') }}
          <BaseIcon name="arrow-right" />
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseIcon from '@/components/base/BaseIcon';
import BaseViewSwitcher from '@/components/base/BaseViewSwitcher.vue';
import { viewLabel } from '@/models/tender-views';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';

export default {
  name: 'TendersHeader',
  components: { BaseIcon, BaseViewSwitcher },
  computed: {
    ...mapGetters('tenderViews', ['activeView']),
    paths() {
      return paths;
    },
    switchesView() {
      return viewport.size === 'phone' && !!this.activeView;
    },
    activeViewLabel() {
      return this.activeView ? viewLabel(this.activeView) : '';
    },
  },
};
</script>

<style lang="scss">
.tenders {
  &__header {
    // One row at every width (title left, keywords shortcut right). Children stretch to the fixed
    // header height so the dividing bar can run full-height; each centers its own content.
    align-items: stretch;
    background: $color-white;
    box-shadow: $shadow-dropdown;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;

    @include phone-page-header;

    &-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-width: 0;
      padding: 16px 32px;
    }

    &-top {
      align-items: center;
      display: flex;
    }

    &-title {
      color: $color-normal;
      display: flex;
      font-family: $font-family-raleway;
      font-size: $font-size-page-title;
      font-weight: normal;
      margin: 0;
      overflow: hidden;
      position: relative;
      text-overflow: ellipsis;
      text-transform: uppercase;
      white-space: nowrap;
    }

    // Full-height dividing bar between the inbox title and the keywords shortcut.
    &-actions {
      align-items: center;
      border-left: 1px solid $color-border;
      display: flex;
      flex: none;
      padding: 0 40px;
    }
  }

  // Keywords shortcut — icon + title with a "see all" link beneath (mirrors the contact block on
  // the keywords page). The whole block is clickable; the dividing bar lives on __header-actions.
  &__keywords {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
    padding: 4px 0;
    text-align: left;

    &-title {
      align-items: center;
      color: $color-normal;
      display: flex;
      font-weight: bold;
      gap: 6px;

      .feather {
        height: 18px;
        width: 18px;
      }
    }

    &-link {
      align-items: center;
      color: $color-normal-pale;
      display: flex;
      font-size: $font-size-small;
      gap: 4px;
      transition: color 150ms ease;

      .feather {
        height: 15px;
        width: 15px;
      }
    }

    &:hover &-link {
      color: $color-accent;
    }
  }

  @media screen and (max-width: 1080px) {
    &__header {
      &-title {
        font-size: 25px;
      }
    }
  }

  // Phones: the two-line shortcut won't fit beside the title, so collapse it to just its icon
  // (still a full tap target routing to the keywords page). Drop the divider and tighten padding.
  @media screen and (max-width: 650px) {
    &__header-content {
      padding: 16px;

      @include screen-below('tablet') {
        padding: 0 $phone-page-gutter;
      }
    }

    &__header-title {
      @include phone-page-title;
    }

    &__header-actions {
      border-left: none;
      padding: 0 16px 0 8px;
    }

    &__keywords {
      gap: 0;
      padding: 8px;

      &-title span,
      &-link {
        display: none;
      }

      &-title .feather {
        height: 24px;
        width: 24px;
      }
    }
  }
}
</style>
